import axios from "axios";
import { useCallback } from "react";
import SideNavbar from "../../components/common/SideNavbar";
import TopNavbar from "../../components/common/TopNavbar";
import { useEffect, useState } from "react";
import { Button, Spinner, Modal } from "react-bootstrap";
import { toast, ToastContainer } from "react-toastify";
import DataTable from "react-data-table-component";
import { tableCustomStyles } from "../../utils/TableCustomStyles";

function PackageTypeMaster() {
  const [menuDrawerShow, setMenuDrawerShow] = useState(true);
  const [data, setData] = useState([]);
  const [dataPending, setDataPending] = useState(true);
  const [currentUserRolePermissionData, setCurrentUserRolePermissionData] =
    useState([]);
  const [dataFromTopNavBar, setDataFromTopNavBar] = useState("");
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const user_role = sessionStorage["userrole"];
  const user_id = sessionStorage["id"];
  const [loggedInUser, setLoggedInUser] = useState(null);

  const [canvasFullScreen, setCanvasFullScreen] = useState(false);
  const [showAddCanvas, setShowAddCanvas] = useState(false);
  const [showUpdateCanvas, setShowUpdateCanvas] = useState(false);

  // Add form state
  const [name, setName] = useState("");
  const [code, setCode] = useState("");
  const [status, setStatus] = useState("ACTIVE");

  // Add field errors
  const [showNameError, setShowNameError] = useState(false);
  const [showCodeError, setShowCodeError] = useState(false);

  // Add validation summary
  const [showValidationError, setShowValidationError] = useState(false);
  const [isValidationErrorVisible, setIsValidationErrorVisible] =
    useState(false);

  // Update form state
  const [updateId, setUpdateId] = useState("");
  const [updateName, setUpdateName] = useState("");
  const [updateCode, setUpdateCode] = useState("");
  const [updateStatus, setUpdateStatus] = useState("ACTIVE");

  // Update field errors
  const [showUpdateNameError, setShowUpdateNameError] = useState(false);
  const [showUpdateCodeError, setShowUpdateCodeError] = useState(false);

  // Update validation summary
  const [showUpdateValidationError, setShowUpdateValidationError] =
    useState(false);
  const [isUpdateValidationErrorVisible, setIsUpdateValidationErrorVisible] =
    useState(false);

  // Loaders
  const [showAddLoader, setShowAddLoader] = useState(false);
  const [showUpdateLoader, setShowUpdateLoader] = useState(false);
  const [showDeleteLoader, setShowDeleteLoader] = useState(false);
  const [deleteRecordId, setDeleteRecordId] = useState(null);

  // ========== Validation Helpers ==========
  const hasAnyAddError = () => showNameError || showCodeError;

  const buildAddErrorMessages = (overrides = {}) => {
    const errors = [];
    const n = "name" in overrides ? overrides.name : name;
    const c = "code" in overrides ? overrides.code : code;
    if (!n) errors.push("Package name is required");
    if (!c) errors.push("Package code is required");
    return errors;
  };

  const hasAnyUpdateError = () => showUpdateNameError || showUpdateCodeError;

  const buildUpdateErrorMessages = (overrides = {}) => {
    const errors = [];
    const n = "updateName" in overrides ? overrides.updateName : updateName;
    const c = "updateCode" in overrides ? overrides.updateCode : updateCode;
    if (!n) errors.push("Package name is required");
    if (!c) errors.push("Package code is required");
    return errors;
  };

  const handleCheckValidationClick = () =>
    setIsValidationErrorVisible(!isValidationErrorVisible);

  const handleUpdateCheckValidationClick = () =>
    setIsUpdateValidationErrorVisible(!isUpdateValidationErrorVisible);

  // ========== Safe JSON parser ==========
  const get_user = (userjson) => {
    if (!userjson) return "None";
    try {
      const user_data = JSON.parse(userjson);
      return user_data?.name || "None";
    } catch {
      return "None";
    }
  };

  // ========== API calls ==========
  const getLoggenInUser = useCallback(async () => {
    try {
      const response = await axios.get(
        process.env.REACT_APP_BACKEND_API_URL + "/user/user/" + user_id,
      );
      if (response) {
        setLoggedInUser(response.data);
      }
    } catch (err) {
      toast.error("Error in fetching user data! Try again.");
    }
  }, [user_id]);

  const getPackage = async () => {
    try {
      const response = await axios.get(
        process.env.REACT_APP_BACKEND_API_URL + "/master/packagetype/",
      );
      if (response) {
        setData(response.data);
        setDataPending(false);
      }
    } catch (err) {
      toast.error("Error in fetching package type data! Try again.");
    }
  };

  const getCurrentUserRolePermission = async (roles) => {
    try {
      const response = await axios.get(
        process.env.REACT_APP_BACKEND_API_URL +
          "/user/role/getuserrolepermission?roles=" +
          roles,
      );

      const role_permissions_array = [];
      for (let i = 0; i < response.data.length; i++) {
        for (let j = 0; j < response?.data[i]?.permissions_array.length; j++) {
          if (
            !role_permissions_array.includes(
              response?.data[i]?.permissions_array?.[j],
            )
          ) {
            role_permissions_array.push(
              response?.data[i]?.permissions_array?.[j],
            );
          }
        }
      }

      if (role_permissions_array) {
        setCurrentUserRolePermissionData(role_permissions_array);
      }
    } catch (err) {
      toast.error("Error in fetching user role data! Try again.");
    }
  };

  useEffect(() => {
    getPackage();
    getLoggenInUser();
  }, [getLoggenInUser]);

  useEffect(() => {
    getCurrentUserRolePermission(user_role);
  }, [user_role]);

  // ========== Delete handlers ==========
  const handleDeleteModalClose = () => setShowDeleteModal(false);

  const handleDeleteModalOpen = (row) => {
    setShowDeleteModal(true);
    setDeleteRecordId(row.id);
  };

  const deletePackage = async () => {
    setShowDeleteLoader(true);
    try {
      await axios.delete(
        process.env.REACT_APP_BACKEND_API_URL +
          "/master/packagetype/" +
          deleteRecordId +
          "/",
        { headers: { "content-type": "application/json" } },
      );
      toast.success("Package Deleted Successfully");
      getPackage();
    } catch (err) {
      toast.error("Error deleting package! Try again.");
    } finally {
      setShowDeleteLoader(false);
      setShowDeleteModal(false);
    }
  };

  // ========== Add logic ==========
  const resetAddForm = () => {
    setName("");
    setCode("");
    setStatus("ACTIVE");
    setShowNameError(false);
    setShowCodeError(false);
    setShowValidationError(false);
    setIsValidationErrorVisible(false);
  };

  const handleAddModalClose = () => {
    resetAddForm();
    setShowAddCanvas(false);
    setCanvasFullScreen(false);
  };

  const addPackage = async (e) => {
    e.preventDefault();

    setShowNameError(!name);
    setShowCodeError(!code);

    if (!name || !code) {
      setShowValidationError(true);
      return;
    }

    setShowAddLoader(true);

    const payload = {
      name: name,
      code: code,
      status: status,
      created_by: JSON.stringify({"id": loggedInUser?.id, "username": loggedInUser?.username, "name": loggedInUser?.name, "email": loggedInUser?.email, "mobile": loggedInUser?.mobile, "user_type": loggedInUser?.user_type}),
    };

    try {
      const response = await axios.post(
        process.env.REACT_APP_BACKEND_API_URL + "/master/packagetype/",
        payload,
        { headers: { "content-type": "application/json" } },
      );
      if (response.status === 200 || response.status === 201) {
        toast.success("Package Created Successfully");
        resetAddForm();
        getPackage();
        setShowAddCanvas(false);
      }
    } catch (err) {
      toast.error("Error creating package! Try again.");
    } finally {
      setShowAddLoader(false);
    }
  };

  // ========== Update logic ==========
  const resetUpdateForm = () => {
    setUpdateId("");
    setUpdateName("");
    setUpdateCode("");
    setUpdateStatus("ACTIVE");
    setShowUpdateNameError(false);
    setShowUpdateCodeError(false);
    setShowUpdateValidationError(false);
    setIsUpdateValidationErrorVisible(false);
  };

  const handleUpdateModalClose = () => {
    resetUpdateForm();
    setShowUpdateCanvas(false);
    setCanvasFullScreen(false);
  };

  const handleSetUpdateData = (row) => {
    setUpdateId(row.id);
    setUpdateName(row.name);
    setUpdateCode(row.code);
    setUpdateStatus(row.status);
    setShowUpdateNameError(false);
    setShowUpdateCodeError(false);
    setShowUpdateValidationError(false);
    setIsUpdateValidationErrorVisible(false);
  };

  const updatePackage = async (e) => {
    e.preventDefault();

    setShowUpdateNameError(!updateName);
    setShowUpdateCodeError(!updateCode);

    if (!updateName || !updateCode) {
      setShowUpdateValidationError(true);
      return;
    }

    setShowUpdateLoader(true);

    const payload = {
      name: updateName,
      code: updateCode,
      status: updateStatus,
      modified_by: JSON.stringify({"id": loggedInUser?.id, "username": loggedInUser?.username, "name": loggedInUser?.name, "email": loggedInUser?.email, "mobile": loggedInUser?.mobile, "user_type": loggedInUser?.user_type}),
    };

    try {
      const response = await axios.put(
        process.env.REACT_APP_BACKEND_API_URL +
          "/master/packagetype/" +
          updateId +
          "/",
        payload,
        { headers: { "content-type": "application/json" } },
      );
      if (response.status === 200) {
        toast.success("Package Updated Successfully");
        handleUpdateModalClose();
        getPackage();
      }
    } catch (err) {
      toast.error("Error updating package! Try again.");
    } finally {
      setShowUpdateLoader(false);
    }
  };

  function handleDataFromTopNavBar(permissiondata) {
    setDataFromTopNavBar(permissiondata);
  }

  // ========== Table columns ==========
  const columns = [
    {
      name: "Package Name",
      selector: (row) => row.name,
      sortable: true,
    },
    {
      name: "Package Code",
      selector: (row) => row.code,
      sortable: true,
    },
    {
      name: "Status",
      selector: (row) => row.status,
      sortable: true,
    },
    {
      name: "Created By",
      selector: (row) => row.created_by_data?.name,
      sortable: true,
    },
    {
      name: "Created On",
      selector: (row) => row.created_on.slice(0, 10),
      sortable: true,
    },
    {
      name: "Action",
      right: true,
      selector: (row) => (
        <div>
          {currentUserRolePermissionData?.includes(
            "master:packagetype:update",
          ) ? (
            <Button
              className="btn btn-sm btn-inox-green border-0"
              title="Update"
              data-bs-toggle="offcanvas"
              data-bs-target="#updateSection"
              onClick={() => handleSetUpdateData(row)}
            >
              <i className="fa fa-edit"></i>
            </Button>
          ) : (
            ""
          )}

          {currentUserRolePermissionData?.includes(
            "master:packagetype:delete",
          ) ? (
            <Button
              className="btn btn-sm bg-danger border-0 mx-2"
              title="Delete"
              onClick={() => handleDeleteModalOpen(row)}
            >
              <i className="fa fa-trash"></i>
            </Button>
          ) : (
            ""
          )}
        </div>
      ),
    },
  ];

  return (
    <div className="page-content-bg">
      <ToastContainer />
      <TopNavbar
        sidebarCollapsed={menuDrawerShow}
        topNavbarStatic={true}
        sendDataToParent={handleDataFromTopNavBar}
      />
      <SideNavbar
        sidebarCollapsed={menuDrawerShow}
        activeMenu="Master"
        activeSubMenu="PackageMaster"
        activeSubMenuLevel1=""
        permissionFromTopNavbar={dataFromTopNavBar}
      />

      <div
        className={
          menuDrawerShow
            ? "row main-content-collapsed text-left py-0"
            : "row main-content text-left py-0"
        }
      >
        <div className="col-12 page-header">
          <div className="row">
            <div className="col-6 py-1">
              <Button
                className="btn btn-sm btn-inox-green border-0 mx-2"
                onClick={() => setMenuDrawerShow(!menuDrawerShow)}
              >
                {menuDrawerShow ? (
                  <span>
                    <i className="fa fa-chevron-left mx-1"></i>
                  </span>
                ) : (
                  <span>
                    <i className="fa fa-chevron-right mx-1"></i>
                  </span>
                )}
              </Button>
              <span className="page-header-text">Package Type Master</span>
            </div>

            <div className="col-6 py-1" style={{ paddingRight: 30 }}>
              {currentUserRolePermissionData?.includes(
                "master:packagetype:add",
              ) ? (
                <Button
                  className="btn btn-sm btn-inox-green border-0 px-3 float-right"
                  data-bs-toggle="offcanvas"
                  data-bs-target="#addSection"
                >
                  <i
                    className="fa fa-plus fa-lg text-right"
                    style={{ paddingRight: 10 }}
                  ></i>
                  <span className="fw-bold">Add Package Type</span>
                </Button>
              ) : (
                ""
              )}
            </div>
          </div>
        </div>

        <div
          className="col-12 text-left"
          style={{
            marginTop: -5,
            paddingLeft: 20,
            paddingRight: 30,
            zIndex: 100,
          }}
        >
          <DataTable
            columns={columns}
            data={data}
            progressPending={dataPending}
            progressComponent={
              <Spinner
                className="mx-5 text-inox-green table-spinner"
                as="span"
                animation="border"
                size="lg"
                role="status"
                aria-hidden="true"
              />
            }
            noDataComponent={
              <p className="text-center table-no-data">No Data Found</p>
            }
            pagination
            highlightOnHover
            fixedHeader
            customStyles={tableCustomStyles}
            dense
          />
        </div>
      </div>

      {/* -------------------- Delete Modal -------------------- */}
      <Modal
        size="sm"
        show={showDeleteModal}
        onHide={handleDeleteModalClose}
        centered
      >
        <Modal.Header closeButton>
          <Modal.Title>Delete</Modal.Title>
        </Modal.Header>
        <Modal.Body>Do you want to delete this record?</Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={handleDeleteModalClose}>
            Close
          </Button>
          <Button variant="danger" onClick={deletePackage}>
            {showDeleteLoader ? (
              <Spinner
                className="mx-4"
                as="span"
                animation="border"
                size="sm"
                role="status"
                aria-hidden="true"
              />
            ) : (
              "Delete"
            )}
          </Button>
        </Modal.Footer>
      </Modal>

      {/* -------------------- Update Canvas -------------------- */}
      <div
        className={
          canvasFullScreen && showUpdateCanvas
            ? "offcanvas offcanvas-bottom h-100 show"
            : !canvasFullScreen && showUpdateCanvas
              ? "offcanvas offcanvas-bottom h-60 show"
              : "offcanvas offcanvas-bottom h-60"
        }
        id="updateSection"
        data-bs-scroll="true"
        tabIndex="-1"
        aria-labelledby="offcanvasBottomLabel"
      >
        <div className="offcanvas-header w-100">
          <div className="row w-100">
            <div className="col-6">
              <h4 id="offcanvasBottomLabel" className="float-left">
                Update Record
              </h4>
            </div>
            <div className="col-6">
              <button
                type="button"
                className="btn btn-danger btn-sm float-right"
                data-bs-dismiss="offcanvas"
                aria-label="Close"
                title="Close"
                onClick={handleUpdateModalClose}
              >
                <i className="fa fa-times text-white"></i>
              </button>

              {canvasFullScreen ? (
                <button
                  type="button"
                  className="btn btn-inox-green btn-sm mx-2 float-right"
                  title="Half Screen"
                  onClick={() => [
                    setCanvasFullScreen(false),
                    setShowUpdateCanvas(true),
                  ]}
                >
                  <i className="fa fa-window-minimize text-white"></i>
                </button>
              ) : (
                <button
                  type="button"
                  className="btn btn-inox-green btn-sm mx-2 float-right"
                  title="Full Screen"
                  onClick={() => [
                    setCanvasFullScreen(true),
                    setShowUpdateCanvas(true),
                  ]}
                >
                  <i className="fa fa-window-maximize text-white"></i>
                </button>
              )}
            </div>
          </div>
        </div>

        <div className="offcanvas-body">
          <form onSubmit={(e) => updatePackage(e)}>
            <div className="row">
              <div
                className={`form-group ${canvasFullScreen ? "col-md-3" : "col-md-3"} text-left mb-4`}
              >
                <label className="required-label" htmlFor="update_name">
                  Package Name:{" "}
                  <span className="text-danger">
                    {showUpdateNameError ? "Package name is required" : ""}
                  </span>
                </label>
                <input
                  type="text"
                  className="form-control form-control-sm"
                  id="update_name"
                  name="update_name"
                  placeholder="Enter Package Name"
                  value={updateName}
                  onChange={(e) => {
                    const val = e.target.value;
                    setUpdateName(val);
                    setShowUpdateNameError(!val);
                    setShowUpdateValidationError(false);
                    setIsUpdateValidationErrorVisible(false);
                  }}
                  onBlur={(e) => setShowUpdateNameError(!e.target.value)}
                />
              </div>

              <div
                className={`form-group ${canvasFullScreen ? "col-md-3" : "col-md-3"} text-left mb-4`}
              >
                <label className="required-label" htmlFor="update_code">
                  Package Code:{" "}
                  <span className="text-danger">
                    {showUpdateCodeError ? "Package code is required" : ""}
                  </span>
                </label>
                <input
                  type="text"
                  className="form-control form-control-sm"
                  id="update_code"
                  name="update_code"
                  placeholder="Enter Package Code"
                  value={updateCode}
                  onChange={(e) => {
                    const val = e.target.value;
                    setUpdateCode(val);
                    setShowUpdateCodeError(!val);
                    setShowUpdateValidationError(false);
                    setIsUpdateValidationErrorVisible(false);
                  }}
                  onBlur={(e) => setShowUpdateCodeError(!e.target.value)}
                />
              </div>

              <div
                className={`form-group ${canvasFullScreen ? "col-md-3" : "col-md-3"} text-left mb-4`}
              >
                <label htmlFor="update_status">Status:</label>
                <select
                  className="form-select form-select-sm"
                  id="update_status"
                  name="update_status"
                  value={updateStatus}
                  onChange={(e) => setUpdateStatus(e.target.value)}
                >
                  <option value="ACTIVE">ACTIVE</option>
                  <option value="INACTIVE">INACTIVE</option>
                </select>
              </div>

              <div
                className={`form-group ${canvasFullScreen ? "col-md-3" : "col-md-3"} text-left mb-4`}
              >
                <Button
                  type="button"
                  className="btn btn-inox-green border-0 canvas-button btn-sm mt-4"
                  onClick={(e) => updatePackage(e)}
                  disabled={hasAnyUpdateError() || showUpdateLoader}
                >
                  {showUpdateLoader ? (
                    <Spinner
                      className="mx-4"
                      as="span"
                      animation="border"
                      size="sm"
                      role="status"
                      aria-hidden="true"
                    />
                  ) : (
                    <>
                      <i className="fa fa-edit"></i>
                      <span className="mx-2">Update</span>
                    </>
                  )}
                </Button>

                {(hasAnyUpdateError() || showUpdateValidationError) && (
                  <div className="error-tooltip-wrapper">
                    <Button
                      style={{
                        backgroundColor: "#C21E22",
                        border: "#f82e2e",
                      }}
                      title="Show Errors"
                      className="mx-2"
                      onClick={handleUpdateCheckValidationClick}
                      onMouseLeave={() =>
                        setIsUpdateValidationErrorVisible(false)
                      }
                    >
                      <i className="fa fa-exclamation"></i>
                    </Button>
                    {isUpdateValidationErrorVisible && (
                      <div className="validation-error-tooltip">
                        <ul className="mb-0 px-4">
                          {buildUpdateErrorMessages().map((error, index) => (
                            <li key={index} className="text-left small pb-0">
                              {error}
                            </li>
                          ))}
                        </ul>
                      </div>
                    )}
                  </div>
                )}
              </div>
            </div>
          </form>
        </div>
      </div>
      {/* -------------------- End Update Canvas -------------------- */}

      {/* -------------------- Add Canvas -------------------- */}
      <div
        className={
          canvasFullScreen && showAddCanvas
            ? "offcanvas offcanvas-bottom h-100 show"
            : !canvasFullScreen && showAddCanvas
              ? "offcanvas offcanvas-bottom h-60 show"
              : "offcanvas offcanvas-bottom h-60"
        }
        data-bs-scroll="true"
        tabIndex="-1"
        id="addSection"
        aria-labelledby="offcanvasBottomLabel"
      >
        <div className="offcanvas-header w-100">
          <div className="row w-100">
            <div className="col-6">
              <h4 id="offcanvasBottomLabel" className="float-left">
                Add New Record
              </h4>
            </div>
            <div className="col-6">
              <button
                type="button"
                className="btn btn-danger btn-sm float-right"
                data-bs-dismiss="offcanvas"
                aria-label="Close"
                title="Close"
                onClick={handleAddModalClose}
              >
                <i className="fa fa-times text-white"></i>
              </button>

              {canvasFullScreen ? (
                <button
                  type="button"
                  className="btn btn-inox-green btn-sm mx-2 float-right"
                  title="Half Screen"
                  onClick={() => [
                    setCanvasFullScreen(false),
                    setShowAddCanvas(true),
                  ]}
                >
                  <i className="fa fa-window-minimize text-white"></i>
                </button>
              ) : (
                <button
                  type="button"
                  className="btn btn-inox-green btn-sm mx-2 float-right"
                  title="Full Screen"
                  onClick={() => [
                    setCanvasFullScreen(true),
                    setShowAddCanvas(true),
                  ]}
                >
                  <i className="fa fa-window-maximize text-white"></i>
                </button>
              )}
            </div>
          </div>
        </div>

        <div className="offcanvas-body">
          <form onSubmit={(e) => addPackage(e)}>
            <div className="row">
              <div
                className={`form-group ${canvasFullScreen ? "col-md-3" : "col-md-3"} text-left mb-4`}
              >
                <label className="required-label" htmlFor="add_name">
                  Package Name:{" "}
                  <span className="text-danger">
                    {showNameError ? "Package name is required" : ""}
                  </span>
                </label>
                <input
                  type="text"
                  className="form-control form-control-sm"
                  id="add_name"
                  name="add_name"
                  placeholder="Enter Package Name"
                  value={name}
                  onChange={(e) => {
                    const val = e.target.value;
                    setName(val);
                    setShowNameError(!val);
                    setShowValidationError(false);
                    setIsValidationErrorVisible(false);
                  }}
                  onBlur={(e) => setShowNameError(!e.target.value)}
                />
              </div>

              <div
                className={`form-group ${canvasFullScreen ? "col-md-3" : "col-md-3"} text-left mb-4`}
              >
                <label className="required-label" htmlFor="add_code">
                  Package Code:{" "}
                  <span className="text-danger">
                    {showCodeError ? "Package code is required" : ""}
                  </span>
                </label>
                <input
                  type="text"
                  className="form-control form-control-sm"
                  id="add_code"
                  name="add_code"
                  placeholder="Enter Package Code"
                  value={code}
                  onChange={(e) => {
                    const val = e.target.value;
                    setCode(val);
                    setShowCodeError(!val);
                    setShowValidationError(false);
                    setIsValidationErrorVisible(false);
                  }}
                  onBlur={(e) => setShowCodeError(!e.target.value)}
                />
              </div>

              <div
                className={`form-group ${canvasFullScreen ? "col-md-3" : "col-md-3"} text-left mb-4`}
              >
                <label htmlFor="add_status">Status:</label>
                <select
                  className="form-select form-select-sm"
                  id="add_status"
                  name="add_status"
                  value={status}
                  onChange={(e) => setStatus(e.target.value)}
                >
                  <option value="ACTIVE">ACTIVE</option>
                  <option value="INACTIVE">INACTIVE</option>
                </select>
              </div>

              <div
                className={`form-group ${canvasFullScreen ? "col-md-3" : "col-md-3"} text-left mb-4`}
              >
                <Button
                  type="button"
                  className="btn btn-inox-green border-0 canvas-button btn-sm mt-4"
                  onClick={(e) => addPackage(e)}
                  disabled={hasAnyAddError() || showAddLoader}
                >
                  {showAddLoader ? (
                    <Spinner
                      className="mx-4"
                      as="span"
                      animation="border"
                      size="sm"
                      role="status"
                      aria-hidden="true"
                    />
                  ) : (
                    <>
                      <i className="fa fa-plus"></i>
                      <span className="mx-2">Add</span>
                    </>
                  )}
                </Button>

                {(hasAnyAddError() || showValidationError) && (
                  <div className="error-tooltip-wrapper">
                    <Button
                      style={{
                        backgroundColor: "#C21E22",
                        border: "#f82e2e",
                      }}
                      title="Show Errors"
                      className="mx-2"
                      onClick={handleCheckValidationClick}
                      onMouseLeave={() => setIsValidationErrorVisible(false)}
                    >
                      <i className="fa fa-exclamation"></i>
                    </Button>
                    {isValidationErrorVisible && (
                      <div className="validation-error-tooltip">
                        <ul className="mb-0 px-4">
                          {buildAddErrorMessages().map((error, index) => (
                            <li key={index} className="text-left small pb-0">
                              {error}
                            </li>
                          ))}
                        </ul>
                      </div>
                    )}
                  </div>
                )}
              </div>
            </div>
          </form>
        </div>
      </div>
      {/* -------------------- End Add Canvas -------------------- */}
    </div>
  );
}

export default PackageTypeMaster;
